'use client';

import clsx from 'clsx';
import { trackEvent } from '../../../lib/analytics/posthog';
import { EVENTS } from '../../../lib/analytics/events';

const PRICE_OPTIONS = [
  { value: '', label: 'Cualquier precio' },
  { value: '15000', label: 'Hasta $15.000' },
  { value: '25000', label: 'Hasta $25.000' },
  { value: '40000', label: 'Hasta $40.000' }
];

// Los rubros más buscados primero, con nombres cortos para que entren en una fila.
const ORDER = [
  'Uñas',
  'Pestañas',
  'Peluquería',
  'Cejas',
  'Barbería',
  'Depilación',
  'Tratamientos faciales',
  'Maquillaje'
];
const SHORT = {
  Peluquería: 'Pelo',
  'Tratamientos faciales': 'Faciales',
  'Tratamientos corporales': 'Corporales'
};
const rank = (name) => {
  const i = ORDER.indexOf(name);
  return i === -1 ? ORDER.length : i;
};

export const sortCategories = (categories) =>
  [...categories].sort((a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name, 'es'));

export const categoryLabel = (name) => SHORT[name] || name;

/** «Turnos hoy», los rubros y el precio. El panel de escritorio y la barra del teléfono usan la misma. */
export function FilterChips({
  categories,
  selectedCategory,
  onSelectCategory,
  todayOnly,
  onToggleToday,
  maxPrice,
  onPriceChange,
  withAll = false,
  withToday = true,
  className
}) {
  const priceLabel = PRICE_OPTIONS.find((o) => o.value === maxPrice && o.value)?.label || 'Precio';
  const priceChip = (
    <label className={clsx('chip chip--select', maxPrice && 'chip--active')}>
      <span aria-hidden="true">{priceLabel}</span>
      <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M5.3 7.3a1 1 0 0 1 1.4 0L10 10.6l3.3-3.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4Z" />
      </svg>
      <select aria-label="Precio" value={maxPrice} onChange={(e) => onPriceChange(e.target.value)}>
        {PRICE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <div className={clsx('filters', className)} role="group" aria-label="Filtros">
      {withToday && <TodayChip active={todayOnly} onToggle={onToggleToday} />}
      {/* Escritorio: el precio va antes que los rubros (con 12 rubros quedaba fuera de la vista). */}
      {!withAll && priceChip}
      {withAll && (
        <button
          type="button"
          aria-pressed={!selectedCategory}
          className={clsx('chip', !selectedCategory && 'chip--active')}
          onClick={() => onSelectCategory(null)}
        >
          Todo
        </button>
      )}
      {categories === null && <span className="filters__wait" aria-hidden="true" />}
      {(categories || []).map((cat) => {
        const active = selectedCategory?.id === cat.id;
        return (
          <button
            key={cat.id}
            type="button"
            aria-pressed={active}
            className={clsx('chip', active && 'chip--active')}
            onClick={() => {
              const next = active ? null : cat;
              onSelectCategory(next);
              trackEvent(EVENTS.DISCOVERY_CATEGORY_FILTERED, {
                category_name: next?.name || 'all',
                category_slug: cat.slug || cat.name.toLowerCase()
              });
            }}
          >
            {categoryLabel(cat.name)}
          </button>
        );
      })}
      {/* Teléfono: «Todo», los rubros y el precio al final, como en la propuesta. */}
      {withAll && priceChip}
    </div>
  );
}

export function TodayChip({ active, onToggle }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={clsx('chip chip--today', active && 'chip--active')}
      onClick={() => {
        trackEvent(EVENTS.DISCOVERY_FILTER_CHANGED, { filter: 'turnos_hoy', value: !active });
        onToggle();
      }}
    >
      <span className="chip__dot" aria-hidden="true" />
      Turnos hoy
    </button>
  );
}

/** Negocios o Trabajos. */
export function ViewTabs({ view, onSelect, className }) {
  return (
    <div className={clsx('seg', className)} role="tablist" aria-label="Cómo ver los resultados">
      {[
        ['negocios', 'Negocios'],
        ['trabajos', 'Trabajos']
      ].map(([value, label]) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={view === value}
          className={clsx('seg__tab', view === value && 'is-on')}
          onClick={() => onSelect(value)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
